import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const ScrollKanbanBlock = ({ 
  sections, 
  kanbanProps = {},
  className = "",
  contentClassName = "",
  fullWidth = false,
  initialColors = ["slate", "violet", "blue", "emerald", "orange", "rose"],
  endingColor = "violet",
  showGraph = true,
  graphData = null,
  ...props 
}) => {
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [currentKanbanProps, setCurrentKanbanProps] = React.useState(null);
  const containerRef = React.useRef(null);

  const colorSchemes = {
    slate: {
      column: 'bg-slate-200 dark:bg-slate-700',
      card: 'bg-slate-300 dark:bg-slate-600',
      title: 'bg-slate-500 dark:bg-slate-400',
      content: 'bg-slate-400 dark:bg-slate-500',
      circle: 'bg-slate-100 dark:bg-slate-800'
    },
    violet: {
      column: 'bg-violet-100 dark:bg-violet-900/30',
      card: 'bg-violet-200 dark:bg-violet-800/40',
      title: 'bg-violet-400 dark:bg-violet-600',
      content: 'bg-violet-300 dark:bg-violet-700',
      circle: 'bg-violet-50 dark:bg-violet-900/50'
    },
    blue: {
      column: 'bg-blue-100 dark:bg-blue-900/30',
      card: 'bg-blue-200 dark:bg-blue-800/40',
      title: 'bg-blue-400 dark:bg-blue-600',
      content: 'bg-blue-300 dark:bg-blue-700',
      circle: 'bg-blue-50 dark:bg-blue-900/50'
    },
    emerald: {
      column: 'bg-emerald-100 dark:bg-emerald-900/30',
      card: 'bg-emerald-200 dark:bg-emerald-800/40',
      title: 'bg-emerald-400 dark:bg-emerald-600',
      content: 'bg-emerald-300 dark:bg-emerald-700',
      circle: 'bg-emerald-50 dark:bg-emerald-900/50'
    },
    orange: {
      column: 'bg-orange-100 dark:bg-orange-900/30',
      card: 'bg-orange-200 dark:bg-orange-800/40',
      title: 'bg-orange-400 dark:bg-orange-600',
      content: 'bg-orange-300 dark:bg-orange-700',
      circle: 'bg-orange-50 dark:bg-orange-900/50'
    },
    rose: {
      column: 'bg-rose-100 dark:bg-rose-900/30',
      card: 'bg-rose-200 dark:bg-rose-800/40',
      title: 'bg-rose-400 dark:bg-rose-600',
      content: 'bg-rose-300 dark:bg-rose-700',
      circle: 'bg-rose-50 dark:bg-rose-900/50'
    }
  };

  // Memoized static random data that only gets generated once
  const staticRandomData = useMemo(() => {
    const maxColumns = 6;
    const finalColumns = 3;
    const items = 12;
    const columnArray = Array.from({ length: maxColumns }, (_, i) => i + 1);
    const itemArray = Array.from({ length: items }, (_, i) => i + 1);

    // Random column widths for masonry layout - only generated once
    const baseWidth = 100 / maxColumns;
    const variation = 0.3;
    
    let widths = columnArray.map(() => {
      const randomFactor = 0.7 + Math.random() * variation * 2;
      return baseWidth * randomFactor;
    });
    
    const total = widths.reduce((sum, width) => sum + width, 0);
    const randomColumnWidths = widths.map(width => (width / total) * 100);

    // Random column heights for masonry layout - only generated once
    const randomColumnHeights = columnArray.map(() => Math.random() * 40 + 60);

    // Calculate final positions for each item (end state)
    const finalPositions = itemArray.map((_, index) => {
      const columnIndex = index % finalColumns;
      const itemInColumn = Math.floor(index / finalColumns);
      
      return {
        x: columnIndex * (100 / finalColumns), // percentage
        y: itemInColumn * 80 + 8 + (showGraph ? 65 : 0), // pixels (added 65px for graph banner if enabled)
        columnIndex
      };
    });

    // Calculate initial positions (start state) - random masonry
    const initialPositions = itemArray.map((_, index) => ({
      x: Math.random() * 80, // percentage
      y: Math.random() * 300 + 20, // pixels
      width: 15 + Math.random() * 10,
      columnIndex: Math.floor(Math.random() * maxColumns)
    }));

    // Calculate the final layout height based on the final state (added graph banner height if enabled)
    const finalContainerHeight = Math.ceil(items / finalColumns) * 80 + 80 + (showGraph ? 65 : 0);

    // Generate default graph data if not provided
    const defaultGraphData = Array.from({ length: 20 }, () => Math.random() * 30 + 10);

    return {
      randomColumnWidths,
      randomColumnHeights,
      initialPositions,
      finalPositions,
      finalContainerHeight,
      graphData: graphData || defaultGraphData
    };
  }, [showGraph, graphData]); // Add dependencies for graph-related props

  // Scroll handling effect
  React.useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;
      const windowHeight = window.innerHeight;

      if (containerTop <= 0 && containerBottom > windowHeight) {
        const scrollableHeight = containerRect.height - windowHeight;
        const progress = Math.abs(containerTop) / scrollableHeight;
        setScrollProgress(Math.max(0, Math.min(1, progress)));
      } else if (containerBottom <= windowHeight && containerTop < windowHeight) {
        setScrollProgress(1);
      } else if (containerTop > 0) {
        setScrollProgress(0);
      }
    };

    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, []);

  // Calculate kanban props based on scroll progress
  React.useEffect(() => {
    const easeInOutCubic = (t) => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const smoothProgress = easeInOutCubic(scrollProgress);
    
    const minColumns = 3;
    const maxColumns = 6;
    const columns = Math.round(maxColumns - (smoothProgress * (maxColumns - minColumns)));
    const items = 12;
    
    let colorScheme = endingColor;
    let colorArray = null;
    
    // Very slowly fade the colors - start transition much earlier
    if (scrollProgress < .9) {
      colorArray = initialColors;
    }
    
    const newProps = {
      ...kanbanProps,
      columns,
      items,
      colorScheme,
      colorArray,
      scrollProgress: smoothProgress
    };

    setCurrentKanbanProps(prev => {
      if (!prev || 
          prev.columns !== newProps.columns || 
          prev.colorScheme !== newProps.colorScheme ||
          (prev.colorArray === null) !== (newProps.colorArray === null) ||
          Math.abs(prev.scrollProgress - newProps.scrollProgress) > 0.01) {
        return newProps;
      }
      return prev;
    });
  }, [scrollProgress, kanbanProps, endingColor, initialColors]);

  // Get current active section
  const getCurrentSectionIndex = () => {
    const sectionProgress = scrollProgress * (sections.length - 1);
    return Math.round(sectionProgress);
  };

  // Memoized component calculations using static random data
  const kanbanData = useMemo(() => {
    if (!currentKanbanProps) return null;

    const { columns, items, colorArray, colorScheme } = currentKanbanProps;
    const columnArray = Array.from({ length: columns }, (_, i) => i + 1);
    const itemArray = Array.from({ length: items }, (_, i) => i + 1);

    const colors = colorSchemes[colorScheme] || colorSchemes.slate;

    return {
      columnArray,
      itemArray,
      colors,
      columns,
      items,
      colorArray,
      colorScheme
    };
  }, [currentKanbanProps, colorSchemes]);

  // Function to interpolate between initial and final positions based on scroll progress
  const interpolatePosition = (itemIndex, progress) => {
    const { initialPositions, finalPositions } = staticRandomData;
    const initial = initialPositions[itemIndex];
    const final = finalPositions[itemIndex];
    
    // Use easing function for smoother transitions
    const easeProgress = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    return {
      x: initial.x + (final.x - initial.x) * easeProgress,
      y: initial.y + (final.y - initial.y) * easeProgress,
      columnIndex: Math.round(initial.columnIndex + (final.columnIndex - initial.columnIndex) * easeProgress)
    };
  };

  // Function to interpolate between colors very slowly
  const interpolateColors = (initialColor, finalColor, progress) => {
    // Start color transition much earlier and make it very gradual
    const colorTransitionStart = 0.3; // Start at 30% scroll
    const colorTransitionEnd = 0.95; // End at 95% scroll
    
    if (progress < colorTransitionStart) {
      return initialColor;
    }
    
    if (progress > colorTransitionEnd) {
      return finalColor;
    }
    
    // Very slow color transition over a long scroll range
    const colorProgress = (progress - colorTransitionStart) / (colorTransitionEnd - colorTransitionStart);
    const easeColorProgress = colorProgress * colorProgress * (3.0 - 2.0 * colorProgress); // Smooth step
    
    return {
      column: progress > colorTransitionEnd ? finalColor.column : 'bg-transparent',
      card: progress > colorTransitionEnd * 0.95 ? finalColor.card : initialColor.card,
      title: progress > colorTransitionEnd * 0.97 ? finalColor.title : initialColor.title,
      content: progress > colorTransitionEnd * 0.98 ? finalColor.content : initialColor.content,
      circle: progress > colorTransitionEnd * 0.99 ? finalColor.circle : initialColor.circle
    };
  };

  // Function to create graph visualization from data
  const createGraphVisualization = (data) => {
    if (!data || data.length === 0) return [];

    const width = 400; // percentage
    const height = 60; // pixels
    const barWidth = width / data.length * 0.7; // 70% width for bars, leaving gaps
    const gapWidth = width / data.length * 0.3; // 30% for gaps
    const startX = 10; // Start position
    
    // Normalize data to 0-1 range
    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue || 1; // Avoid division by zero
    
    return data.map((value, i) => {
      const normalizedHeight = (value - minValue) / range;
      const x = startX + i * (barWidth + gapWidth);
      const barHeight = height * (0.3 + normalizedHeight * 0.6); // 30% to 90% of height
      const y = height - barHeight; // Start from bottom
      
      return {
        x,
        y,
        width: barWidth,
        height: barHeight,
        value
      };
    });
  };

  // Render kanban skeleton
  const renderKanban = () => {
    if (!kanbanData) return null;

    const { columnArray, itemArray, colors, columns, colorArray } = kanbanData;
    const { randomColumnWidths, randomColumnHeights, finalContainerHeight, graphData: currentGraphData } = staticRandomData;

    // Get column layout
    const getColumnLayout = () => {
      // if (colorArray && colorArray.length > 0) {
      //   let leftPosition = 0;
      //   return columnArray.map((_, index) => {
      //     const width = randomColumnWidths[index];
      //     const left = leftPosition;
      //     leftPosition += width;
          
      //     return {
      //       left: `${left}%`,
      //       width: `${width - 1}%`,
      //       height: randomColumnHeights[index]
      //     };
      //   });
      // } else {
        return columnArray.map((_, index) => ({
          left: `${(index * 100) / columns}%`,
          width: `${100 / columns - 1}%`,
          height: 100
        }));
      // }
    };

    // Get item position using interpolation
    const getItemPosition = (itemIndex) => {
      const scrollProgress = currentKanbanProps.scrollProgress;
      const position = interpolatePosition(itemIndex, scrollProgress);
      
      return {
        x: `${position.x}%`,
        y: `${position.y}px`,
        columnIndex: position.columnIndex
      };
    };

    // Get item colors with slow interpolation
    const getItemColors = (itemIndex) => {
      const scrollProgress = currentKanbanProps.scrollProgress;
      
      if (colorArray && colorArray.length > 0) {
        const initialColorName = colorArray[itemIndex % colorArray.length];
        const initialColorScheme = colorSchemes[initialColorName] || colors;
        const finalColorScheme = colors;
        
        return interpolateColors(initialColorScheme, finalColorScheme, scrollProgress);
      }
      return colors;
    };

    // Get column background
    const getColumnBackground = () => {
      const scrollProgress = currentKanbanProps.scrollProgress;
      
      if (colorArray && colorArray.length > 0) {
        // Show column background at same time as graph
        if (scrollProgress >= .9) {
          return colors.column;
        }
        return 'bg-transparent';
      }
      return colors.column;
    };

    // Get graph banner opacity
    const getGraphOpacity = () => {
      const scrollProgress = currentKanbanProps.scrollProgress;
      return scrollProgress >= .9 ? 1 : 0;
    };

    // Get item width with interpolation
    const getItemWidth = (itemIndex) => {
      const scrollProgress = currentKanbanProps.scrollProgress;
      const { initialPositions } = staticRandomData;
      const initial = initialPositions[itemIndex];
      const finalWidth = 96 / 3 - 2; // Final width for 3 columns
      
      const easeProgress = scrollProgress < 0.5 ? 2 * scrollProgress * scrollProgress : 1 - Math.pow(-2 * scrollProgress + 2, 2) / 2;
      const currentWidth = initial.width + (finalWidth - initial.width) * easeProgress;
      
      return `${currentWidth}%`;
    };

    const columnLayout = getColumnLayout();
    const graphOpacity = getGraphOpacity();

    // Create graph bars from data
    const graphBars = createGraphVisualization(currentGraphData);

    return (
      <div className="relative w-full p-4 container" style={{ height: `${finalContainerHeight}px` }}>
        {/* Graph Banner - only visible when showGraph is true */}
        {showGraph && (
          <motion.div
            className={`absolute top-4 left-0 right-0 mr-1 -mt-2 ${colors.column} rounded-t-lg  overflow-hidden border-b-2 border-b-violet-300 dark:border-b-violet-600`}
            style={{ 
              height: '60px'
            }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ 
              opacity: graphOpacity,
              y: graphOpacity > 0 ? 0 : -20
            }}
            transition={{ 
              duration: 0.6,
              ease: "easeOut"
            }}
          >
            {/* Top left slim rectangle */}
            <motion.div
              className={`absolute top-2 left-2 ${colors.title} rounded`}
              style={{
                width: '55px',
                height: '8px'
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: graphOpacity > 0 ? 1 : 0,
                scale: graphOpacity > 0 ? 1 : 0.8
              }}
              transition={{ 
                duration: 0.4,
                delay: 0.2,
                ease: "easeOut"
              }}
            />

            {/* Top right rounded pills */}
            <div className="absolute top-2 right-2 flex space-x-1">
              {[0, 1, 2].map((pillIndex) => (
                <motion.div
                  key={pillIndex}
                  className={`${colors.content} rounded-full`}
                  style={{
                    width: '20px',
                    height: '10px'
                  }}
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ 
                    opacity: graphOpacity > 0 ? 1 : 0,
                    scale: graphOpacity > 0 ? 1 : 0.6
                  }}
                  transition={{ 
                    duration: 0.3,
                    delay: 0.3 + (pillIndex * 0.1),
                    ease: "easeOut"
                  }}
                />
              ))}
            </div>

            <svg 
              className="w-full h-[60px]" 
              viewBox="0 0 420 60" 
              preserveAspectRatio="none"
              style={{ width: '100%' }}
            >
              {graphBars.map((bar, index) => (
                <motion.rect
                  key={index}
                  x={bar.x}
                  y={bar.y}
                  width={bar.width}
                  height={bar.height}
                  fill="currentColor"
                  className="text-current dark:[color:rgb(139_92_246/0.6)]"
                  style={{ 
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                    color: colorSchemes[endingColor]?.content?.replace('bg-', '').includes('violet') ? 'rgb(139 92 246 / 0.4)' : 'rgb(148 163 184 / 0.4)'
                  }}
                  initial={{ 
                    height: 0,
                    y: 40
                  }}
                  animate={{ 
                    height: graphOpacity > 0 ? bar.height : 0,
                    y: graphOpacity > 0 ? bar.y : 40
                  }}
                  transition={{ 
                    duration: 0.4,
                    delay: graphOpacity > 0 ? index * 0.05 : 0,
                    ease: "easeOut"
                  }}
                />
              ))}
            </svg>
          </motion.div>
        )}

        {/* Column backgrounds */}
        {columnArray.map((column, index) => {
          const layout = columnLayout[index];
          const columnOpacity = getGraphOpacity();
          
          return (
            <motion.div 
              key={`column-${column}`} 
              className={`absolute ${getColumnBackground()} rounded-lg`}
              style={{
                left: layout.left,
                width: layout.width,
                height: `${layout.height}%`,
                top: showGraph ? '75px' : '4px' // Adjust top position based on graph visibility
              }}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: columnOpacity
              }}
              transition={{ 
                duration: 0.6,
                ease: "easeOut"
              }}
              layout="position"
            />
          );
        })}
        
        {/* Items */}
        {itemArray.map((item, index) => {
          const position = getItemPosition(index);
          const itemColors = getItemColors(index);
          const itemWidth = getItemWidth(index);
          
          return (
            <motion.div
              key={`item-${item}`}
              className={`absolute ${itemColors.card} p-3 rounded-md mt-4 ml-2`}
              style={{
                left: position.x,
                top: position.y,
                width: itemWidth,
                zIndex: 10
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                type: 'spring', 
                stiffness: 200, 
                damping: 25,
                delay: index * 0.05,
                duration: 0.4
              }}
              layout={false} // Disable layout animation since we're manually controlling position
            >
              <div className={`${itemColors.title} h-3 w-3/4 mb-2 rounded`}></div>
              <div className={`${itemColors.content} h-2 w-full mb-1 rounded`}></div>
              <div className={`${itemColors.content} h-2 w-5/6 mb-2 rounded mb-3`}></div>
              <div className={`absolute bottom-2 right-2 ${itemColors.circle} w-3 h-3 rounded-full`}></div>
            </motion.div>
          );
        })}
      </div>
    );
  };

  // Calculate mobile section opacity based on scroll progress
  const getMobileSectionOpacity = (sectionIndex) => {
    if (sections.length === 1) return 1;
    
    const sectionProgress = scrollProgress * sections.length;
    const currentSection = Math.floor(sectionProgress);
    const localProgress = sectionProgress - currentSection;
    
    if (sectionIndex === 0 && currentSection === 0) {
      // First section starts at opacity 1 and fades out
      return localProgress > 0.7 ? (1 - localProgress) / 0.3 : 1;
    }
    
    if (sectionIndex === currentSection) {
      // For the last section, don't fade out at the end
      if (sectionIndex === sections.length - 1) {
        return localProgress < 0.3 ? localProgress / 0.3 : 1;
      }
      return localProgress < 0.3 ? localProgress / 0.3 : 
             localProgress > 0.7 ? (1 - localProgress) / 0.3 : 1;
    }
    
    if (sectionIndex === currentSection + 1 && localProgress > 0.7) {
      return (localProgress - 0.7) / 0.3;
    }
    
    // Keep the last section visible after we've scrolled past all sections
    if (sectionIndex === sections.length - 1 && currentSection >= sections.length - 1) {
      return 1;
    }
    
    return 0;
  };

  // Initial render fallback
  if (!currentKanbanProps) {
    const { finalContainerHeight } = staticRandomData;
    
    return (
      <div 
        ref={containerRef}
        className={`relative ${className}`} 
        style={{ height: `${sections.length * 200}vh` }}
        {...props}
      >
        {/* Mobile-first layout */}
        <div className={`block lg:hidden ${!fullWidth ? 'container mx-auto' : ''}`}>
          {/* All sections stacked */}
          <div className="relative min-h-screen">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                className={`${'index-' + index} absolute inset-0 flex items-center justify-center py-8 sm:px-8`}
                style={{
                  opacity: index === 0 ? 1 : 0,
                  zIndex: index === 0 ? 20 : 10
                }}
              >
                {section}
              </motion.div>
            ))}
            
            {/* Static kanban in middle layer */}
            <div className="absolute inset-0 flex items-center justify-center p-8" style={{ opacity: 0, zIndex: 15 }}>
              <div className="relative w-full max-w-2xl">
                <div className="relative w-full p-4 container" style={{ height: `${finalContainerHeight}px` }}>
                  {showGraph && (
                    <div className="absolute top-0 bg-slate-200 rounded-lg" style={{ left: '0%', width: '100%', height: '50px', opacity: 0 }}></div>
                  )}
                  <div className="absolute bg-slate-200 rounded-lg" style={{ left: '0%', width: '32%', height: '100%', top: showGraph ? '54px' : '4px' }}></div>
                  <div className="absolute bg-slate-200 rounded-lg" style={{ left: '33%', width: '32%', height: '100%', top: showGraph ? '54px' : '4px' }}></div>
                  <div className="absolute bg-slate-200 rounded-lg" style={{ left: '66%', width: '32%', height: '100%', top: showGraph ? '54px' : '4px' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop layout */}
        <div className={`hidden lg:block relative ${!fullWidth ? 'container mx-auto' : ''}`}>        
          <div className="sticky top-0 right-0 h-[100vh] w-1/2 flex items-center justify-center py-8 sm:px-8 float-right">
            <div className="relative w-full max-w-2xl">
              <div className="relative w-full p-4 container" style={{ height: `${finalContainerHeight}px` }}>
                {showGraph && (
                  <div className="absolute top-0 bg-slate-200 rounded-lg" style={{ left: '0%', width: '100%', height: '50px', opacity: 0 }}></div>
                )}
                <div className="absolute bg-slate-200 rounded-lg" style={{ left: '0%', width: '32%', height: '100%', top: showGraph ? '54px' : '4px' }}></div>
                <div className="absolute bg-slate-200 rounded-lg" style={{ left: '33%', width: '32%', height: '100%', top: showGraph ? '54px' : '4px' }}></div>
                <div className="absolute bg-slate-200 rounded-lg" style={{ left: '66%', width: '32%', height: '100%', top: showGraph ? '54px' : '4px' }}></div>
              </div>
            </div>
          </div>

          <div className={`w-1/2 relative ${contentClassName}`}>
            {sections.map((section, index) => (
              <div key={index} className="flex items-center relative" style={{ height: '100vh' }}>
                <div className="relative z-20 py-10 px-4 -ml-4 rounded">
                  {section}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`} 
      style={{ height: `${sections.length * 100}vh` }}
      {...props}
    >
      {/* Mobile-first layout with layered sections */}
      <div className={`block lg:hidden ${!fullWidth ? 'container mx-auto' : ''} sticky top-0`}>
        <div className="relative h-[100vh] ">
          {/* All sections stacked and animated based on scroll */}
          {sections.map((section, index) => {
            const opacity = getMobileSectionOpacity(index);
            const isKanbanSection = index === Math.floor(sections.length / 1); // Middle section for kanban
            
            return (
              <motion.div
                key={index}
                className={`${'index-' + index} ${isKanbanSection ? 'isKanbanSection' : ''} absolute inset-0 flex items-center justify-center py-8 sm:px-8`}
                animate={{
                  opacity: isKanbanSection ? 0 : opacity,
                }}
                transition={{
                  duration: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                style={{
                  zIndex: isKanbanSection ? 10 : (opacity > 0.5 ? 20 : 15)
                }}
              >
                {section}
              </motion.div>
            );
          })}
          
          {/* Kanban animation in middle layer */}
          <motion.div
            className="absolute bg-background inset-0 flex items-center justify-center p-8"
            animate={{
              opacity: scrollProgress > 0.2 && scrollProgress < 0.8 ? 1 : 0,
              scale: scrollProgress > 0.2 && scrollProgress < 0.8 ? 1 : 0.9
            }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            style={{ zIndex: 25 }}
          >
            <motion.div
              className="relative w-full max-w-2xl"
              layout
              transition={{ 
                type: "spring",
                stiffness: 100,
                damping: 25,
                mass: 0.8,
                restDelta: 0.001
              }}
            >
              {renderKanban()}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className={`hidden lg:block relative ${!fullWidth ? 'container mx-auto' : ''}`}>        
        <div className="sticky top-0 right-0 h-[100vh] w-1/2 flex items-center justify-center p-8 float-right">
          <motion.div
            className="relative w-full max-w-2xl"
            layout
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 25,
              mass: 0.8,
              restDelta: 0.001
            }}
          >
            {renderKanban()}
          </motion.div>
        </div>

        <div className={`w-1/2 relative ${contentClassName}`}>
          {sections.map((section, index) => {
            const currentIndex = getCurrentSectionIndex();
            const isActive = index === currentIndex;
            
            return (
              <div key={index} className="flex items-center relative" style={{ height: '100vh' }}>
                <motion.div 
                  className="relative z-20 py-10 px-4 -ml-4 rounded"
                  animate={{
                    opacity: isActive ? 1 : 0.5,
                    scale: isActive ? 1 : 0.98,
                  }}
                  transition={{
                    duration: 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  {section}
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ScrollKanbanBlock;
